import { useState, useEffect } from "react";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import { toast } from "sonner";
import { Modal } from "react-bootstrap";
import { SubmitHandler, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useAcademicYear } from "../../../core/common/context/AcademicYearContext";
import { getAllEvaluationPeriodByLevel } from "../../../api/generalParam";
import { createUE } from "../../../api/ue";
import withPermission from "../../../core/common/autorisations/withPermission";
import {
  Action,
  buildPermission,
  buildPermissionContext,
  Resource,
} from "../../../core/common/autorisations/permissionsMap";

//Typage des props parents
type UUID = string;
type Props = {
  show: boolean;
  levelID: UUID;
  classID: UUID;
  classLabel: string;
  onClose: () => void;
  onSaved?: () => void;
};
interface Period {
  id: string;
  id_level: string;
  label: string;
  abbreviation: string;
  startDate: String;
  endDate: String;
  statut: boolean;
}

const CreateUEModal = ({
  show,
  onClose,
  onSaved,
  classID,
  classLabel,
  levelID,
}: Props) => {
  //Initialisation de l'année academmique actuelle
  const { selectedYear, setSelectedYear } = useAcademicYear();
  const [periods, setPeriods] = useState<Period[]>([]);

  const PeriodTypeEnum = z.union([
    z.literal("UE Majeure"),
    z.literal("UE Mineure"),
    z.literal("UE Libre"),
  ]);

  //Schéma de notre formulaire
  const createSchema = z.object({
    label: z.string().nonempty("Le libellé de l'UE est requis"),
    codeUE: z.string().nonempty("Le code de l'UE est requis"),
    id_evaluationPeriod: z
      .string()
      .nonempty("La période d'évaluation est requise"),
    type: PeriodTypeEnum,
  });

  type FormFields = z.infer<typeof createSchema>;

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<FormFields>({
    resolver: zodResolver(createSchema),
  });

  //Traitement après validation du formulaire de création d'un niveau
  const onSubmit: SubmitHandler<FormFields> = async (data) => {
    try {
      const finalData = {
        ...data,
        id_class: classID ?? "", // 👈 Ajout manuel ici
      };
      //Envoi des datas pour création vers le backend
      await createUE(finalData);

      reset(); //On réinitialise les champs du formulaire
      if (onSaved) onSaved(); // On fait un reload de la liste

      toast.success("Unité d'Enseignement ajoutée !"); //Toast de succès

      onClose(); // On ferme le modal
    } catch (error: any) {
      // Si erreur (Toast Error)
      // console.log(error)
      toast.error(
        error.response?.data?.message ||
          error.response?.data?.error ||
          "Une erreur s'est produit, Réessayer plus tard",
      );
    }
  };

  //On recupère la liste des période en appelant sa fonction
  useEffect(() => {
    // On attend que selectedYear soit défini
    if (!selectedYear?.id) return;

    getAllEvaluationPeriodByLevel(
      levelID,
      buildPermissionContext(Resource.UE, Action.CREATE),
    ).then((periods) => {
      // console.log(periods)
      setPeriods(periods.data);
    });
  }, [show]);

  return (
    <>
      <Modal show={show} onHide={() => onClose()}>
        <Modal.Header closeButton>
          <Modal.Title>Création d'un UE ({classLabel})</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <form
            id="level-form"
            className="needs-validation"
            onSubmit={handleSubmit(onSubmit)}
          >
            <div className="row row-cols-xxl-5 row-cols-md-6">
              <div className="col-xxl-12 col-xl-12 col-md-12">
                <div className="mb-3">
                  <label className="form-label">Type UE</label>
                  <select
                    {...register("type")}
                    aria-label="Default select example"
                    className={
                      errors.type ? "form-select is-invalid" : "form-select"
                    }
                  >
                    <option value={""}>Sélectionner</option>
                    <option value={"UE Majeure"}>UE Majeure</option>
                    <option value={"UE Mineure"}>UE Mineure</option>
                    <option value={"UE Libre"}>UE Libre</option>
                  </select>
                  {errors.type && (
                    <div className="invalid-feedback">
                      {errors.type.message}
                    </div>
                  )}
                </div>
              </div>
              <div className="col-xxl-12 col-xl-12 col-md-12">
                <div className="mb-3">
                  <label className="form-label" htmlFor="validationServer01">
                    Libellé
                  </label>
                  <input
                    {...register("label")}
                    type="text"
                    className={
                      errors.label ? "form-control is-invalid" : "form-control"
                    }
                    placeholder="Ex: Salle A1 ou Salle Labo"
                  />
                  {errors.label && (
                    <div className="invalid-feedback">
                      {errors.label.message}
                    </div>
                  )}
                </div>
              </div>

              <div className="col-xxl-12 col-xl-12 col-md-12">
                <div className="mb-3">
                  <label className="form-label" htmlFor="validationServer01">
                    Code UE
                  </label>
                  <input
                    {...register("codeUE")}
                    type="text"
                    className={
                      errors.codeUE ? "form-control is-invalid" : "form-control"
                    }
                    placeholder="Ex: UE-MGT"
                  />
                  {errors.codeUE && (
                    <div className="invalid-feedback">
                      {errors.codeUE.message}
                    </div>
                  )}
                </div>
              </div>

              <div className="col-xxl-12 col-xl-12 col-md-12">
                <div className="mb-3">
                  <label className="form-label">Période d'Évaluation</label>
                  <select
                    {...register("id_evaluationPeriod")}
                    aria-label="Default select example"
                    className={
                      errors.id_evaluationPeriod
                        ? "form-select is-invalid"
                        : "form-select"
                    }
                  >
                    <option value={""}>Sélectionner</option>
                    {periods.map((period) => (
                      <option key={period.id} value={period.id}>
                        {period.label}
                      </option>
                    ))}
                  </select>
                  {errors.id_evaluationPeriod && (
                    <div className="invalid-feedback">
                      {errors.id_evaluationPeriod.message}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </form>
        </Modal.Body>
        <Modal.Footer>
          <button
            type="button"
            className="btn btn-light me-3"
            onClick={() => {
              reset();
              onClose();
            }}
          >
            Annuler
          </button>

          {isSubmitting ? (
            <button
              className="btn btn-primary-light"
              type="button"
              disabled={false}
            >
              <span
                className="spinner-border spinner-border-sm align-middle me-1"
                role="status"
                aria-hidden="true"
              />
              Chargement...
            </button>
          ) : (
            <button
              type="submit"
              form="level-form" // 🔗 liaison au formulaire
              className="btn btn-primary"
              // disabled={isSubmitting||!selectedYear?.statut}
              disabled={isSubmitting}
            >
              Enregistrer
            </button>
          )}
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default withPermission(
  CreateUEModal,
  [buildPermission(Resource.UE, Action.CREATE)],
  "masquer",
);
